/**
 * ContactPanel.js — slide-in contact info panel.
 *
 * Shows: avatar, name, phone, assignment, last seen, bot status,
 * tags (with inline add/remove), custom fields, actions. Cached in
 * localStorage for instant load on PWA revisit.
 */

import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';

import * as api from '../services/api.js';
import { showToast, $mutedChats, isChatMuted, toggleChatMute } from '../stores/ui.js';
import { getChannelInfo, getChannelColor, getChannelLabel, $channelMeta } from '../services/channel-colors.js';
import { resolveAgent, $team } from '../services/team-directory.js';
import { useStore } from '@nanostores/preact';

const CACHE_KEY = 'inbox_v2_contacts';
// Global catalog caches — shared across ContactPanel instances for the
// session. Tags + custom field definitions change rarely so one fetch per
// session is plenty.
const TAG_CATALOG_KEY = 'inbox_v2_tag_catalog';
const CF_CATALOG_KEY  = 'inbox_v2_cf_catalog';
let _tagCatalogPromise = null; // dedupe in-flight fetches
let _cfCatalogPromise  = null;

/**
 * Fetch the account's global tag catalog once per session.
 * Returns a Map<tagId, { id, name, color, icon }>.
 */
async function _loadTagCatalog() {
  // Try localStorage first for instant paint
  try {
    const cached = localStorage.getItem(TAG_CATALOG_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Kick a background refresh but return the cached map immediately
      if (!_tagCatalogPromise) _tagCatalogPromise = _fetchTagCatalog().catch(() => {});
      return new Map(parsed.map(t => [t._id || t.id, t]));
    }
  } catch {}
  if (!_tagCatalogPromise) _tagCatalogPromise = _fetchTagCatalog();
  const list = await _tagCatalogPromise;
  return new Map((list || []).map(t => [t._id || t.id, t]));
}

async function _fetchTagCatalog() {
  const raw = await api.fetchAllTags();
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  try { localStorage.setItem(TAG_CATALOG_KEY, JSON.stringify(list)); } catch {}
  return list;
}

/**
 * Fetch the account's custom-field catalog (definitions). Session-cached
 * in localStorage the same way tags are. Returns an array of
 * { id, name, type, description } entries.
 */
async function _loadCfCatalog() {
  try {
    const cached = localStorage.getItem(CF_CATALOG_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (!_cfCatalogPromise) _cfCatalogPromise = _fetchCfCatalog().catch(() => {});
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch {}
  if (!_cfCatalogPromise) _cfCatalogPromise = _fetchCfCatalog();
  return await _cfCatalogPromise;
}

async function _fetchCfCatalog() {
  const raw = await api.fetchAllCustomFields();
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  try { localStorage.setItem(CF_CATALOG_KEY, JSON.stringify(list)); } catch {}
  return list;
}

/**
 * Extract { id: value } pairs from whatever shape the contact GET returns.
 * The live CRM returns CF values at `data.custom_field_hash` as a flat object
 * (confirmed 2026-04-14 via probe: { "city": "Mumbai", "event_organiser": "ODIV Test" }).
 * Falls back to several other shapes for safety.
 */
function _extractCfValues(raw) {
  if (!raw) return {};
  const hash = raw.data?.custom_field_hash
            || raw.custom_field_hash
            || raw.data?.customFields
            || raw.customFields;
  if (hash && typeof hash === 'object' && !Array.isArray(hash)) return hash;
  return {};
}

function _loadCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); } catch { return {}; }
}
function _saveCache(phone, data) {
  try {
    const cache = _loadCache();
    cache[phone] = { ...data, _cachedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

function getInitials(name) {
  const parts = (name || '?').trim().split(' ');
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

function formatLastSeen(ts) {
  if (!ts || ts <= 0) return '';
  const d = new Date(ts * 1000);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'Active now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * Extract an array of tag IDs from whatever shape the API returns us.
 * Possible shapes seen in the wild:
 *   - { tags: ["id1", "id2"] }                         (string ids)
 *   - { tags: [{ _id: "id1", name: "X" }] }            (objects)
 *   - { data: { tags: [...] } }                        (wrapped)
 *   - ["id1", ...]                                     (raw array)
 *   - { data: { tags_modified: [null], tags: [...] } } (observed live)
 */
function _extractTagIds(raw) {
  if (!raw) return [];
  const val = raw.data?.tags ?? raw.data ?? raw.tags ?? raw;
  const arr = Array.isArray(val) ? val : [];
  return arr
    .map(t => {
      if (typeof t === 'string') return t;
      if (t && typeof t === 'object') return t._id || t.id || null;
      return null;
    })
    .filter(Boolean);
}

export function ContactPanel({ chatId, phone, chat, onClose }) {
  // Subscribe to channel meta so dashboard label edits propagate live
  useStore($channelMeta);
  // #180 — re-render when mute state flips so the row label tracks.
  useStore($mutedChats);
  const muted = isChatMuted(chatId);

  const cached = _loadCache()[phone] || {};
  // tagIds = current IDs applied to this contact
  // tagCatalog = Map<id, {name, color, icon}> for the whole account
  const [tagIds, setTagIds]             = useState(cached.tagIds || []);
  const [tagCatalog, setTagCatalog]     = useState(new Map());
  // cfValues = { [fieldId]: currentValue } for THIS contact only
  // cfCatalog = full list of field definitions for the account
  const [cfValues, setCfValues]         = useState(cached.cfValues || {});
  const [cfCatalog, setCfCatalog]       = useState([]);
  const [loading, setLoading]           = useState(true);
  // Inline editor state
  const [addingTag, setAddingTag]       = useState(false);
  const [tagMutating, setTagMutating]   = useState(null); // id being added/removed
  const [editingCf, setEditingCf]       = useState(null); // id of field currently being edited
  const [cfDraft, setCfDraft]           = useState('');   // draft value of the field under edit
  const [cfSaving, setCfSaving]         = useState(false);
  // #162 — close chat confirmation modal visibility
  const [closeConfirm, setCloseConfirm] = useState(false);
  const [closing, setClosing]           = useState(false);

  const handleCloseChat = async () => {
    if (closing) return;
    setClosing(true);
    try {
      await api.closeChat(chatId, phone);
      api.cancelAllScheduledMessages(phone).catch(() => {});
      showToast('Conversation closed', 2400);
      setCloseConfirm(false);
      // Collapse the Contact panel — the user has finished with this chat.
      if (onClose) onClose();
    } catch (e) {
      console.warn('[v2 contact] closeChat failed:', e.message);
      showToast('Close failed', 2000);
    } finally {
      setClosing(false);
    }
  };

  useEffect(() => {
    if (!chatId) return;
    let cancelled = false;

    // Progressive loading — each fetch updates its own slice of state as
    // soon as it resolves. On cold-start Netlify functions the three calls
    // can take 1.5–4s EACH, so waiting for all of them via Promise.all is
    // visibly sluggish (Loading… for 4+ seconds before ANYTHING appears).
    // Progressive commits get the catalog + tags + CFs on screen in parallel.
    let done = 0;
    const markDone = () => {
      done += 1;
      if (done >= 3 && !cancelled) setLoading(false);
    };

    api.fetchTags(chatId)
      .then(v => {
        if (cancelled) return;
        const ids = _extractTagIds(v);
        setTagIds(ids);
        _saveCache(phone, { tagIds: ids, cfValues });
      })
      .catch(e => console.warn('[v2 contact] fetchTags failed:', e.message))
      .finally(markDone);

    api.fetchCustomFields(chatId)
      .then(v => {
        if (cancelled) return;
        const values = _extractCfValues(v);
        setCfValues(values);
        _saveCache(phone, { tagIds, cfValues: values });
      })
      .catch(e => console.warn('[v2 contact] fetchCustomFields failed:', e.message))
      .finally(markDone);

    _loadTagCatalog()
      .then(catalog => {
        if (cancelled) return;
        setTagCatalog(catalog);
      })
      .catch(e => console.warn('[v2 contact] _loadTagCatalog failed:', e.message));

    _loadCfCatalog()
      .then(catalog => {
        if (cancelled) return;
        setCfCatalog(catalog);
      })
      .catch(e => console.warn('[v2 contact] _loadCfCatalog failed:', e.message));

    return () => { cancelled = true; };
  }, [chatId, phone]);

  // ── Custom field editing ────────────────────────────────────────────────
  // Agents can only UPDATE existing field values. Creating or deleting field
  // definitions stays in the admin panel — explicit user direction 2026-04-14.
  const startEditCf = (fieldId) => {
    setEditingCf(fieldId);
    setCfDraft(cfValues[fieldId] || '');
  };
  const cancelEditCf = () => {
    setEditingCf(null);
    setCfDraft('');
  };
  const saveEditCf = async () => {
    if (!editingCf) return;
    const fieldId = editingCf;
    const newValue = cfDraft.trim();
    const previous = cfValues;
    // Optimistic commit
    const next = { ...cfValues, [fieldId]: newValue };
    if (newValue === '') delete next[fieldId];
    setCfValues(next);
    _saveCache(phone, { tagIds, cfValues: next });
    setCfSaving(true);
    try {
      if (newValue === '') {
        // Empty value → treat as "clear" via DELETE so the key goes away
        await api.removeContactCustomField(chatId, fieldId);
      } else {
        await api.updateContactCustomFields(chatId, [{ id: fieldId, value: newValue }]);
      }
      setEditingCf(null);
      setCfDraft('');
    } catch (e) {
      setCfValues(previous);
      _saveCache(phone, { tagIds, cfValues: previous });
      showToast(`Save failed: ${e.message}`, 3500);
    } finally {
      setCfSaving(false);
    }
  };

  /**
   * Optimistic tag add — flip local state immediately, call API, rollback
   * on failure. Keeps the UI snappy since tag mutations rarely fail.
   * Dropdown stays OPEN after add so the agent can toggle multiple tags
   * in one trip (multicheck pattern, per user spec 2026-04-14).
   */
  const handleAddTag = async (tagId) => {
    if (tagIds.includes(tagId)) return;
    setTagMutating(tagId);
    const previous = tagIds;
    const next = [...tagIds, tagId];
    setTagIds(next);
    _saveCache(phone, { tagIds: next, cfValues });
    try {
      await api.addContactTags(chatId, [tagId]);
    } catch (e) {
      setTagIds(previous);
      _saveCache(phone, { tagIds: previous, cfValues });
      showToast(`Add tag failed: ${e.message}`, 3500);
    } finally {
      setTagMutating(null);
    }
  };

  const handleRemoveTag = async (tagId) => {
    setTagMutating(tagId);
    const previous = tagIds;
    const next = tagIds.filter(id => id !== tagId);
    setTagIds(next);
    _saveCache(phone, { tagIds: next, cfValues });
    try {
      await api.removeContactTags(chatId, [tagId]);
    } catch (e) {
      setTagIds(previous);
      _saveCache(phone, { tagIds: previous, cfValues });
      showToast(`Remove tag failed: ${e.message}`, 3500);
    } finally {
      setTagMutating(null);
    }
  };

  // Available-to-add = catalog minus already-applied
  const applied = tagIds
    .map(id => ({ id, meta: tagCatalog.get(id) }))
    .map(({ id, meta }) => ({
      id,
      name:  meta?.name || 'Unknown tag',
      color: meta?.color || '#6b7fd1',
    }));
  const available = Array.from(tagCatalog.values())
    .filter(t => !tagIds.includes(t._id || t.id))
    .map(t => ({
      id:    t._id || t.id,
      name:  t.name || 'Untitled',
      color: t.color || '#6b7fd1',
    }));

  // Re-render when the team directory hydrates so the assign label
  // flips from "Agent · 69dc2cde" to the resolved name the moment
  // the /team-directory call lands.
  useStore($team);

  const initials = getInitials(chat?.name || phone);
  const lastSeenStr = formatLastSeen(chat?.lastSeen);
  const isUnassigned = !chat?.agent;
  // #159: resolve chat.agent (could be email, team_member_id ObjectId,
  // or the owner's internal id) through the team directory so we show
  // a real name instead of a raw 24-char hex string.
  const assignee = isUnassigned ? null : resolveAgent(chat.agent);

  return html`
    <div class="inbox-sheet-overlay" style="display:block" onClick=${onClose}></div>
    <div class="inbox-contact-panel inbox-panel-open" role="dialog" aria-modal="true" aria-label="Contact info">
      <div class="inbox-header">
        <button class="inbox-header-icon-btn" onClick=${onClose}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
        <h2 class="inbox-header-title">Contact Info</h2>
      </div>

      <div class="inbox-contact-panel-content">
        <!-- Header card -->
        <div class="inbox-contact-card" style="text-align:center">
          <div class="inbox-contact-avatar-lg">${initials}</div>
          <h3 class="inbox-contact-name-lg">${chat?.name || 'Unknown'}</h3>
          <p class="inbox-contact-phone-lg">${phone}</p>
          ${isUnassigned
            ? html`<span class="inbox-badge inbox-badge-unassigned" style="margin-top:0.5rem">Unassigned</span>`
            : html`
                <span
                  class="inbox-badge"
                  title=${assignee?.email || ''}
                  style="margin-top:0.5rem;background:rgba(37,211,102,0.12);color:#25D366"
                >
                  ${assignee?.isYou ? 'Assigned to you' : `Assigned to ${assignee?.label || 'agent'}`}
                </span>
              `
          }
        </div>

        <!-- Details card (channel info + last seen + bot state) -->
        ${(() => {
          // Channel block: only render if we know which channel this chat
          // belongs to (set by messages-service after the first thread load
          // OR by the proxy's /chats annotation). Color square + display
          // name + optional account-owner label as subtitle.
          const channelPhoneId = chat?.channelPhoneId;
          const channelInfo    = channelPhoneId ? getChannelInfo(channelPhoneId) : null;
          const channelName    = channelInfo?.display_name || channelPhoneId || '';
          const channelColor   = channelPhoneId ? getChannelColor(channelPhoneId) : null;
          const channelLabel   = channelPhoneId ? getChannelLabel(channelPhoneId) : null;
          const hasChannel     = !!channelPhoneId;
          const hasAnything    = hasChannel || lastSeenStr || chat?.botActive !== undefined;
          if (!hasAnything) return null;
          return html`
            <div class="inbox-contact-card">
              <h4 class="inbox-contact-section-title">Details</h4>
              ${hasChannel && html`
                <div class="inbox-cf-row" style="display:flex;align-items:center;gap:10px;padding:6px 0">
                  <span
                    title="Channel"
                    style="flex-shrink:0;width:14px;height:14px;border-radius:4px;background:${channelColor};box-shadow:0 1px 3px rgba(0,0,0,0.3)"
                  ></span>
                  <div style="flex:1;min-width:0">
                    <div style="font-size:0.82rem;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${channelName}</div>
                    ${channelLabel && html`
                      <div style="font-size:0.7rem;color:var(--text-muted, #7d8799);margin-top:1px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${channelLabel}</div>
                    `}
                  </div>
                </div>
              `}
              ${lastSeenStr && html`
                <div class="inbox-cf-row">
                  <span class="inbox-cf-label">Last seen</span>
                  <span class="inbox-cf-value">${lastSeenStr}</span>
                </div>
              `}
              ${chat?.botActive !== undefined && html`
                <div class="inbox-cf-row">
                  <span class="inbox-cf-label">Bot</span>
                  <span class="inbox-cf-value">${chat.botActive ? 'Active' : 'Paused'}</span>
                </div>
              `}
            </div>
          `;
        })()}

        <!-- #162 — Actions card: Mute + Close. Transfer and Done moved
             to the Thread header as frequent one-tap actions. Close
             is left here because it's rare and harder to reverse, so
             it gets buried behind the "open contact info" step as an
             intentional friction point.
             #180 — Mute sits above Close. Local-only (localStorage);
             suppresses sound + in-app toast for this contact. The
             row still appears + the unread count still increments. -->
        <div class="inbox-contact-card">
          <h4 class="inbox-contact-section-title">Actions</h4>
          <button
            data-qr-id="contact-mute-toggle"
            onClick=${() => {
              const nowMuted = toggleChatMute(chatId);
              showToast(nowMuted ? 'Chat muted — no sound or banners' : 'Chat unmuted', 1800);
            }}
            style=${`display:flex;align-items:center;gap:10px;width:100%;padding:11px 12px;background:${muted ? 'rgba(37,211,102,0.08)' : 'rgba(255,255,255,0.04)'};border:1px solid ${muted ? 'rgba(37,211,102,0.3)' : 'rgba(255,255,255,0.08)'};border-radius:10px;color:${muted ? '#25D366' : 'var(--text-primary)'};font-size:0.86rem;font-weight:600;cursor:pointer;font-family:inherit;margin-bottom:8px`}
            title=${muted ? 'Click to unmute — sound + banners will resume' : 'Click to mute — suppresses sound + in-app banners for this chat'}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
              ${muted
                ? html`<path d="M15 10v2a3 3 0 0 1-6 0v-2"/><line x1="12" y1="18" x2="12" y2="22"/><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 1 0 6 0V5a3 3 0 0 0-3-3z"/>`
                : html`<line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/>`
              }
            </svg>
            ${muted ? 'Muted · tap to unmute' : 'Mute this chat'}
          </button>
          <button
            class="inbox-close-chat-btn"
            onClick=${() => setCloseConfirm(true)}
            style="display:flex;align-items:center;gap:10px;width:100%;padding:11px 12px;background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.28);border-radius:10px;color:#fbbf24;font-size:0.86rem;font-weight:600;cursor:pointer;font-family:inherit"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
              <path d="M21 8v13H3V8"/>
              <path d="M1 3h22v5H1z"/>
              <line x1="10" y1="12" x2="14" y2="12"/>
            </svg>
            Close conversation
          </button>
          <p style="margin:8px 2px 0;font-size:0.72rem;color:var(--text-muted,#7d8799);line-height:1.4">
            Marks the conversation as ended. The customer can reopen it by sending a new message.
          </p>
        </div>

        <!-- Tags card with dropdown multicheck editor -->
        <div class="inbox-contact-card" style="position:relative">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
            <h4 class="inbox-contact-section-title" style="margin:0">
              Tags${applied.length ? ` (${applied.length})` : ''}
            </h4>
            <button
              onClick=${() => setAddingTag(v => !v)}
              style="background:${addingTag ? '#25D366' : 'rgba(255,255,255,0.08)'};color:${addingTag ? '#0a0c12' : 'var(--text-secondary, #a4adc1)'};border:none;border-radius:8px;padding:5px 11px;font-size:0.72rem;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:4px"
              title=${addingTag ? 'Done editing' : 'Edit tags'}
            >
              ${addingTag ? 'Done' : 'Edit'}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" style="transform:${addingTag ? 'rotate(180deg)' : 'none'};transition:transform 0.15s">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </button>
          </div>

          <!-- Applied chips (always visible) -->
          ${applied.length
            ? html`<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:${addingTag ? '10px' : '0'}">
                ${applied.map(t => html`
                  <span
                    key=${t.id}
                    style="display:inline-flex;align-items:center;gap:6px;background:${t.color}22;border:1px solid ${t.color}55;color:${t.color};border-radius:999px;padding:4px 10px;font-size:0.74rem;font-weight:600"
                  >
                    <span style="width:6px;height:6px;border-radius:50%;background:${t.color};flex-shrink:0"></span>
                    ${t.name}
                  </span>
                `)}
              </div>`
            : !addingTag && html`<p style="font-size:0.78rem;color:var(--text-muted, #7d8799);margin:0">
                ${loading ? 'Loading…' : 'No tags — tap Edit to add some'}
              </p>`
          }

          <!-- Dropdown multicheck (collapsed by default) -->
          ${addingTag && html`
            <div
              class="inbox-tag-dropdown"
              style="background:rgba(0,0,0,0.25);border:1px solid rgba(255,255,255,0.1);border-radius:10px;max-height:260px;overflow-y:auto;margin-top:8px"
            >
              ${tagCatalog.size === 0 && html`
                <div style="padding:14px;text-align:center;font-size:0.78rem;color:var(--text-muted, #7d8799)">
                  ${loading ? 'Loading catalog…' : 'No tags in this account yet'}
                </div>
              `}
              ${Array.from(tagCatalog.values()).map(t => {
                const id      = t._id || t.id;
                const checked = tagIds.includes(id);
                const busy    = tagMutating === id;
                const color   = t.color || '#6b7fd1';
                return html`
                  <button
                    key=${id}
                    disabled=${busy}
                    onClick=${() => checked ? handleRemoveTag(id) : handleAddTag(id)}
                    style="display:flex;align-items:center;gap:10px;width:100%;padding:11px 12px;background:transparent;border:none;border-top:1px solid rgba(255,255,255,0.06);color:inherit;cursor:${busy ? 'wait' : 'pointer'};text-align:left;opacity:${busy ? 0.5 : 1}"
                  >
                    <!-- Checkbox -->
                    <span style="flex-shrink:0;width:18px;height:18px;border-radius:5px;border:2px solid ${checked ? '#25D366' : 'rgba(255,255,255,0.25)'};background:${checked ? '#25D366' : 'transparent'};display:flex;align-items:center;justify-content:center">
                      ${checked && html`
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0a0c12" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      `}
                    </span>
                    <!-- Color dot -->
                    <span style="flex-shrink:0;width:10px;height:10px;border-radius:50%;background:${color}"></span>
                    <!-- Name -->
                    <span style="flex:1;font-size:0.84rem;font-weight:500">${t.name || 'Untitled'}</span>
                  </button>
                `;
              })}
            </div>
          `}
        </div>

        <!-- Custom fields card — one row per DEFINITION, tap to edit the VALUE -->
        <div class="inbox-contact-card">
          <h4 class="inbox-contact-section-title">
            Custom Fields${cfCatalog.length ? ` (${Object.keys(cfValues).length}/${cfCatalog.length})` : ''}
          </h4>
          ${cfCatalog.length === 0
            ? html`<p style="font-size:0.8rem;color:var(--text-muted, #7d8799);margin:0">
                ${loading ? 'Loading…' : 'No custom fields defined for this account'}
              </p>`
            : cfCatalog.map(def => {
                // Backend CFs are untyped — just { name, id }. User direction
                // 2026-04-14: always show the name, let the agent type any
                // string value. No type-based input switching, no validation.
                const id = def.id || def._id;
                const label = def.name || id;
                // CRM stores CF values keyed by id OR by snake_case name
                const value = cfValues[id]
                  ?? cfValues[label]
                  ?? cfValues[(label || '').toLowerCase().replace(/\s+/g, '_')];
                const isEditing = editingCf === id;
                const hasValue = value !== undefined && value !== null && String(value).trim() !== '';
                return html`
                  <div key=${id} class="inbox-cf-row" style="display:flex;align-items:flex-start;gap:8px;padding:10px 0;border-top:1px solid rgba(255,255,255,0.05)">
                    <div style="flex:1;min-width:0">
                      <div style="font-size:0.7rem;color:var(--text-muted, #7d8799);text-transform:uppercase;letter-spacing:0.04em;font-weight:600;margin-bottom:3px">
                        ${label}
                      </div>
                      ${isEditing
                        ? html`
                          <input
                            type="text"
                            value=${cfDraft}
                            onInput=${(e) => setCfDraft(e.target.value)}
                            onKeyDown=${(e) => { if (e.key === 'Enter') saveEditCf(); if (e.key === 'Escape') cancelEditCf(); }}
                            style="width:100%;background:rgba(0,0,0,0.3);border:1px solid rgba(37,211,102,0.5);border-radius:8px;padding:8px 10px;color:inherit;font-size:0.85rem"
                            autofocus
                          />
                          <div style="display:flex;gap:6px;margin-top:6px;justify-content:flex-end">
                            <button
                              onClick=${cancelEditCf}
                              disabled=${cfSaving}
                              style="background:rgba(255,255,255,0.08);color:var(--text-secondary, #a4adc1);border:none;border-radius:6px;padding:5px 11px;font-size:0.72rem;font-weight:600;cursor:pointer"
                            >Cancel</button>
                            <button
                              onClick=${saveEditCf}
                              disabled=${cfSaving}
                              style="background:#25D366;color:#0a0c12;border:none;border-radius:6px;padding:5px 12px;font-size:0.72rem;font-weight:700;cursor:pointer;opacity:${cfSaving ? 0.6 : 1}"
                            >${cfSaving ? 'Saving…' : 'Save'}</button>
                          </div>
                        `
                        : html`
                          <button
                            onClick=${() => startEditCf(id)}
                            style="background:transparent;border:none;color:${hasValue ? 'var(--v2-text, #f1f4f9)' : 'var(--text-muted, #7d8799)'};font-size:0.85rem;padding:0;cursor:pointer;text-align:left;font-style:${hasValue ? 'normal' : 'italic'};word-break:break-word"
                            title="Tap to edit"
                          >
                            ${hasValue ? String(value) : 'Tap to set'}
                          </button>
                        `
                      }
                    </div>
                  </div>
                `;
              })
          }
        </div>
      </div>

      <!-- #162 — Close chat confirmation modal. Heavier than the
           header Done/Transfer flows because Close is (a) rare and
           (b) we don't have a verified "reopen" API, so we treat it
           as slightly destructive and want an intentional confirm. -->
      ${closeConfirm && html`
        <div
          class="inbox-sheet-overlay"
          style="display:block;z-index:310"
          onClick=${() => !closing && setCloseConfirm(false)}
        ></div>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Close conversation"
          style="position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:311;width:min(92vw,360px);background:var(--bg-elev-2,#151925);border:1px solid rgba(255,255,255,0.08);border-radius:16px;padding:20px 18px 14px;box-shadow:0 12px 40px rgba(0,0,0,0.55)"
        >
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
            <div style="width:36px;height:36px;border-radius:50%;background:rgba(245,158,11,0.14);display:flex;align-items:center;justify-content:center;color:#f59e0b;flex-shrink:0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 8v13H3V8"/>
                <path d="M1 3h22v5H1z"/>
                <line x1="10" y1="12" x2="14" y2="12"/>
              </svg>
            </div>
            <h3 style="margin:0;font-size:1rem;font-weight:600;color:var(--text-primary)">Close conversation?</h3>
          </div>
          <p style="margin:0 0 16px;font-size:0.85rem;color:var(--text-secondary,#a4adc1);line-height:1.45">
            This conversation will be marked as ended. The customer can reopen it by sending a new message.
          </p>
          <div style="display:flex;gap:10px;justify-content:flex-end">
            <button
              onClick=${() => setCloseConfirm(false)}
              disabled=${closing}
              style="padding:8px 16px;background:transparent;border:1px solid rgba(255,255,255,0.14);border-radius:10px;color:var(--text-secondary,#a4adc1);font-size:0.85rem;font-weight:500;cursor:pointer;font-family:inherit"
            >
              Cancel
            </button>
            <button
              onClick=${handleCloseChat}
              disabled=${closing}
              style="padding:8px 16px;background:#f59e0b;border:none;border-radius:10px;color:#0a0c12;font-size:0.85rem;font-weight:600;cursor:${closing ? 'wait' : 'pointer'};font-family:inherit;opacity:${closing ? 0.7 : 1}"
            >
              ${closing ? 'Closing…' : 'Close conversation'}
            </button>
          </div>
        </div>
      `}
    </div>
  `;
}
